import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2Icon, Zap, RefreshCw, Download, ChevronDown, X, Ticket } from 'lucide-react';
import { useI18n } from '@/providers/i18n';
import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { ceilCredits } from '@flowmuse/models/cost/credits';
import { hasMultipleImages, getImageCount } from '@/lib/download';
import { toast } from 'sonner';

type GenerateProps = {
  loading: boolean;
  generatedUrl?: string;
  onGenerate: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  imagesCountOptions?: number[];
  currentCount?: number;
  onCountChange?: (count: number) => void;
  estimatedCredits?: number | null;
  passRemaining?: number;
  passCount?: number;
  /** 用户在该模型上的折扣倍数（1 = 不打折）。用于在 fallback 弹窗里展示"原价 × 折扣 = 折后"。 */
  discountMultiplier?: number;
  /** 模型卡折扣倍数（1 = 不打折）。用于在次卡预估旁展示"已应用 xx% off"气泡。 */
  passDiscountMultiplier?: number;
};

export function GenerateItem({
  loading,
  generatedUrl,
  onGenerate,
  onCancel,
  disabled,
  imagesCountOptions,
  currentCount,
  onCountChange,
  estimatedCredits,
  passRemaining,
  passCount = 1,
  discountMultiplier = 1,
  passDiscountMultiplier = 1,
}: GenerateProps) {
  const { t } = useI18n();

  const hasPass = typeof passRemaining === 'number' && passRemaining > 0;
  const isPassOnlyModel = estimatedCredits === null;
  const needsPass = isPassOnlyModel && !hasPass && !loading;
  // 模型卡折扣后可能出现两位小数（如 0.25），round 到 2 位并去掉尾零展示。
  const displayPassCount = String(Math.round(passCount * 100) / 100);
  const passDiscountPct = Number.isFinite(passDiscountMultiplier) && passDiscountMultiplier < 1
    ? Math.round((1 - Math.max(0, passDiscountMultiplier)) * 100)
    : 0;

  // 当存在次卡但本次所需 > 剩余，且模型可回退到通用积分扣费（非 passOnly）且确实会扣积分时，
  // 提示用户：专属积分不足，将继续消耗通用积分。
  const willFallbackToCredits =
    hasPass &&
    !isPassOnlyModel &&
    typeof estimatedCredits === 'number' &&
    estimatedCredits > 0 &&
    typeof passRemaining === 'number' &&
    passRemaining < passCount;

  const [fallbackOpen, setFallbackOpen] = useState(false);

  const fireGenerate = useCallback(() => {
    // Once user clicks Generate, we set a "history barrier" so they can't undo
    // back to the pre-generate canvas state.
    try {
      window.dispatchEvent(new Event("flowmuse:history-barrier"));
    } catch {}
    onGenerate();
  }, [onGenerate]);

  const handleGenerateClick = useCallback(() => {
    if (willFallbackToCredits) {
      setFallbackOpen(true);
      return;
    }
    fireGenerate();
  }, [willFallbackToCredits, fireGenerate]);

  const handleConfirmFallback = useCallback(() => {
    setFallbackOpen(false);
    fireGenerate();
  }, [fireGenerate]);

  const displayPassRemaining = (() => {
    const r = typeof passRemaining === 'number' ? passRemaining : 0;
    return Number.isInteger(r) ? String(r) : r.toFixed(2);
  })();

  // 估算"次卡覆盖一部分后还需要补的通用积分"。
  // 与后端 lib/stripe.ts 中 `cost = cost * (passCount - consumed) / passCount` 对齐。
  // 用 ceilCredits(.., 2) 而非 Math.ceil 避免折扣乘法产生的浮点尾（如 7 × 0.7 = 4.8999... → ceil = 5），
  // 与服务端精度对齐，分位展示。
  const gap = (() => {
    if (typeof estimatedCredits !== 'number' || estimatedCredits <= 0) return { discounted: 0, original: 0 };
    const need = passCount;
    const remain = typeof passRemaining === 'number' ? Math.max(0, passRemaining) : 0;
    if (need <= 0) return { discounted: 0, original: 0 };
    const ratio = Math.max(0, Math.min(1, (need - remain) / need));
    if (ratio <= 0) return { discounted: 0, original: 0 };
    const discounted = Math.max(0.01, ceilCredits(estimatedCredits * ratio, 2));
    // 原价：把 estimatedCredits 反推到不打折状态再按比例乘（avoid 浮点 inverse 误差时直接用 multiplier 反推）。
    // 当 discountMultiplier == 1（无折扣）时 original === discounted。
    const m = Number.isFinite(discountMultiplier) && discountMultiplier > 0 ? discountMultiplier : 1;
    const original = m < 1
      ? Math.max(0.01, ceilCredits((estimatedCredits / m) * ratio, 2))
      : discounted;
    return { discounted, original };
  })();
  const gapCredits = gap.discounted;
  const gapCreditsBeforeDiscount = gap.original;
  const hasDiscount = discountMultiplier < 1 && gapCreditsBeforeDiscount > gapCredits;
  // 展示用：把 0.7 倍 → "7折"，0.85 → "8.5折"，0.5 → "5折"。
  const discountLabel = hasDiscount
    ? (() => {
      const n = discountMultiplier * 10;
      const s = Number.isInteger(n) ? String(n) : n.toFixed(1);
      return t('{{value}}% off', { value: s });
    })()
    : '';

  const fallbackDialog = (
    <AlertDialog open={fallbackOpen} onOpenChange={setFallbackOpen}>
      <AlertDialogContent
        className="sm:max-w-md bg-sidebar/95 backdrop-blur-2xl border border-sidebar-border shadow-none rounded-2xl p-0 gap-0 overflow-hidden"
      >
        <AlertDialogHeader className="px-6 pt-6 pb-2 bg-sidebar/40 backdrop-blur-2xl text-left">
          <AlertDialogTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Ticket size={16} className="text-primary" />
            {t('Your dedicated credits are insufficient — general credits will be used')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-muted-foreground pt-1">
            {t('This action requires {{need}} pass(es); you have {{remain}} remaining. Continuing will deduct {{credits}} general credits to cover the gap.', {
              need: displayPassCount,
              remain: displayPassRemaining,
              credits: gapCredits,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="px-6 py-4 grid grid-cols-2 gap-2 items-stretch">
          <div className="rounded-xl bg-sidebar-accent/20 border border-sidebar-border/30 px-3 py-2.5 flex flex-col">
            <div className="flex items-start justify-between gap-2 h-[14px]">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 leading-[14px]">
                {t('Pass')}
              </div>
            </div>
            <div className="mt-1 text-sm font-semibold tabular-nums">
              {displayPassRemaining} {t('credits')}
            </div>
          </div>
          <div className="rounded-xl bg-sidebar-accent/20 border border-sidebar-border/30 px-3 py-2.5 flex flex-col">
            <div className="flex items-start justify-between gap-2 h-[14px]">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 leading-[14px]">
                {t('credits')}
              </div>
              {hasDiscount && (
                <span className="text-[10px] font-semibold text-primary tabular-nums leading-[14px]">
                  {discountLabel}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm font-semibold tabular-nums">
              {gapCredits} {t('credits')}
              {hasDiscount && (
                <span className="text-[11px] text-muted-foreground/70 font-normal ml-1">
                  ({t('Original Price')} {gapCreditsBeforeDiscount} {t('credits')})
                </span>
              )}
            </div>
          </div>
        </div>
        <AlertDialogFooter className="px-6 pb-6 pt-2 gap-2 sm:gap-2">
          <AlertDialogCancel className="rounded-lg h-9 px-4 text-xs font-bold mt-0 hover:bg-sidebar-accent/30 shadow-none">
            {t('Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmFallback}
            className="rounded-lg h-9 px-4 text-xs font-bold shadow-none"
          >
            {t('Continue with credits')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const handleOpenBuyPasses = useCallback(() => {
    try { window.dispatchEvent(new Event('open_buy_passes')); } catch {}
  }, []);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const inCancelMode = Boolean(loading && onCancel);
  useEffect(() => {
    if (!inCancelMode) setCancelConfirmOpen(false);
  }, [inCancelMode]);

  if (needsPass) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="rounded-xl h-9 px-4 text-xs font-bold shadow-none hover:shadow-none transition-all active:scale-95"
        onClick={handleOpenBuyPasses}
      >
        <Ticket size={14} className="mr-2" />
        {t('Requires Model Pass')}
      </Button>
    );
  }

  // 折扣角标：挂在生成按钮右上角的气泡，标明"已应用 xx% off"。
  // 展示次卡预估时用模型卡折扣，否则用积分折扣。
  const creditsMultiplier = Number.isFinite(discountMultiplier) && discountMultiplier > 0 ? discountMultiplier : 1;
  const creditsDiscountPct = creditsMultiplier < 1 && typeof estimatedCredits === 'number' && estimatedCredits > 0
    ? Math.round((1 - creditsMultiplier) * 100)
    : 0;
  const activeDiscountPct = hasPass ? passDiscountPct : creditsDiscountPct;
  const discountCornerBubble = !loading && activeDiscountPct > 0 ? (
    <span className="pointer-events-none absolute -top-2 -right-2 z-10 whitespace-nowrap rounded-full bg-emerald-500 px-1.5 py-[3px] text-[9px] font-bold leading-none text-white shadow-sm">
      {t('Applied {{pct}}% off', { pct: activeDiscountPct })}
    </span>
  ) : null;

  const creditsBadge =
    !loading ? (
      hasPass ? (
        // 模型卡折扣为 0 时持卡即免单（不扣卡、不扣积分），与服务端扣费口径一致。
        passCount <= 0 ? (
          <span
            className="ml-0 inline-flex items-center whitespace-nowrap px-0 py-0 text-[11px] leading-none tracking-tight text-primary-foreground/90 tabular-nums"
            title={t('Free')}
          >
            <span className="mx-[2px] opacity-70">•</span>
            <span className="ml-[1px] opacity-90">{t('Free')}</span>
          </span>
        ) : (
        <span
          className="ml-0 inline-flex items-center whitespace-nowrap px-0 py-0 text-[11px] leading-none tracking-tight text-primary-foreground/90 tabular-nums"
          title={
            passDiscountPct > 0
              ? `${displayPassCount} ${t('Pass')} (${t('Original Price')} ${Math.round((passCount / passDiscountMultiplier) * 100) / 100})`
              : `${displayPassCount} ${t('Pass')}`
          }
        >
          <span className="mx-[2px] opacity-70">•</span>
          <span className="min-w-[3ch] text-center">{displayPassCount}</span>
          <span className="ml-[1px] opacity-90">{t('Pass')}</span>
        </span>
        )
      ) : typeof estimatedCredits === 'number' ? (() => {
        // 折扣场景：反推原价用于 tooltip，折扣比例由右上角气泡标明。
        const m = creditsMultiplier;
        const showDiscount = m < 1 && estimatedCredits > 0;
        const originalCredits = showDiscount
          ? Math.max(0.01, ceilCredits(estimatedCredits / m, 2))
          : estimatedCredits;
        const tooltip = estimatedCredits <= 0
          ? t('Free')
          : showDiscount
            ? `${estimatedCredits} ${t('credits')} (${t('Original Price')} ${originalCredits})`
            : `${estimatedCredits} ${t('credits')}`;
        return (
          <span
            className="ml-0 inline-flex items-center whitespace-nowrap px-0 py-0 text-[11px] leading-none tracking-tight text-primary-foreground/90 tabular-nums"
            title={tooltip}
          >
            <span className="mx-[2px] opacity-70">•</span>
            {estimatedCredits <= 0 ? (
              <span className="ml-[1px] opacity-90">{t('Free')}</span>
            ) : (
              <>
                <span className="min-w-[3ch] text-center">{estimatedCredits}</span>
                <span className="ml-[1px] opacity-90">{t('credits')}</span>
              </>
            )}
          </span>
        );
      })() : null
    ) : null;

  if (inCancelMode) {
    return (
      <Popover open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="destructive" className="rounded-xl h-9 px-4 text-xs font-bold shadow-none hover:shadow-none transition-all active:scale-95">
            <X size={14} className="mr-2" />
            {t('Cancel')}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto max-w-[220px] p-3 rounded-2xl z-[200]">
          <p className="text-xs text-muted-foreground mb-2.5">{t('Credits will not be refunded after cancellation.')}</p>
          <div className="flex items-center justify-end gap-1.5">
            <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs rounded-lg" onClick={() => setCancelConfirmOpen(false)}>
              {t('Continue generating')}
            </Button>
            <Button size="sm" variant="destructive" className="h-7 px-2.5 text-xs rounded-lg" onClick={() => { setCancelConfirmOpen(false); onCancel?.(); }}>
              {t('Confirm cancel')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // 如果没有数量选项，使用简单按钮
  if (!imagesCountOptions || imagesCountOptions.length <= 1 || !onCountChange) {
    return (
      <>
        <div className="relative inline-flex">
          {loading ? (
            <Button size="sm" variant="default" className="rounded-xl h-9 px-4 text-xs font-bold shadow-none transition-all" disabled>
              <Loader2Icon className="animate-spin mr-2" size={14} />
              {t('Generating...')}
            </Button>
          ) : (
            <Button size="sm" variant="default" className="rounded-xl h-9 px-4 text-xs font-bold shadow-none hover:shadow-none transition-all active:scale-95" onClick={handleGenerateClick} disabled={disabled}>
              {generatedUrl ? <RefreshCw size={14} className="mr-2" /> : <Zap size={14} className="mr-2" />}
              {generatedUrl ? t('Regenerate') : t('Generate')}
              {creditsBadge}
            </Button>
          )}
          {discountCornerBubble}
        </div>
        {fallbackDialog}
      </>
    );
  }

  // 组合按钮：左侧生成，右侧数量选择
  const count = currentCount ?? imagesCountOptions[0];
  
  return (
    <>
    {/* 外层 relative 容器：角标气泡不能挂在 overflow-hidden 的圆角容器里，否则会被裁掉。 */}
    <div className="relative inline-flex">
    <div className="flex items-center rounded-xl bg-primary shadow-none hover:shadow-none transition-all overflow-hidden h-9">
      {/* 左侧：生成按钮 */}
      <Button
        size="sm"
        variant="default"
        className="rounded-none h-9 px-4 text-xs font-bold border-0 border-r border-white/10 hover:bg-white/10"
        onClick={handleGenerateClick}
        disabled={disabled || loading}
      >
        {loading ? (
          <>
            <Loader2Icon className="animate-spin mr-2" size={14} />
            {t('Generating...')}
          </>
        ) : (
          <>
            {generatedUrl ? <RefreshCw size={14} className="mr-2" /> : <Zap size={14} className="mr-2" />}
            {generatedUrl ? t('Regenerate') : t('Generate')}
            {creditsBadge}
          </>
        )}
      </Button>
      
      {/* 右侧：数量选择 */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="default"
            className="rounded-none h-9 px-2 text-[11px] font-bold border-0 tabular-nums hover:bg-white/10"
            disabled={disabled || loading}
          >
            <span className="inline-flex items-center justify-center">
              <span>x</span>
              <span className="min-w-[2ch] text-left">{count}</span>
              <ChevronDown size={12} className="ml-0.5 opacity-80" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-2xl w-[200px] p-2 z-[200]">
          <div className="grid grid-cols-4 gap-1.5">
            {imagesCountOptions.map((opt) => {
              const isActive = opt === count;
              return (
                <Button
                  key={`opt-${opt}`}
                  size="sm"
                  variant="ghost"
                  className={cn(
                    "h-9 px-0 text-[11px] font-bold rounded-lg transition-all",
                    isActive ? "bg-primary text-primary-foreground shadow-none" : "hover:bg-sidebar-accent"
                  )}
                  disabled={disabled || loading}
                  onClick={() => {
                    if (!disabled && !loading) {
                      onCountChange(opt);
                    }
                  }}
                >
                  <span className="inline-flex items-center justify-center tabular-nums">
                    <span>x</span>
                    <span className="min-w-[2ch] text-left">{opt}</span>
                  </span>
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
    {discountCornerBubble}
    </div>
    {fallbackDialog}
    </>
  );
}

type DownloadProps = {
  onDownload: () => void; // 默认 CDN 源
  onDownloadOriginal?: () => void; // 可选：官方/原始源
  onDownloadAll?: () => void; // 可选：全部下载
  onDownloadThumbnail?: () => void | Promise<void>; // 可选：下载当前缩略图（画布呈现）
  onDownloadAllThumbnail?: () => void | Promise<void>; // 可选：下载全部缩略图（画布呈现）
  downloadData?: any; // 下载数据，用于检测是否有多张图片
  disabled?: boolean;
};

export function DownloadItem({
  onDownload,
  onDownloadOriginal,
  onDownloadAll,
  onDownloadThumbnail,
  onDownloadAllThumbnail,
  downloadData,
  disabled,
}: DownloadProps) {
  const { t } = useI18n();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadTimerRef = useRef<number | null>(null);
  const isImage = useMemo(() => {
    try {
      const type = String((downloadData as any)?.type ?? '');
      if (type.startsWith('image/')) return true;
    } catch {}
    return Boolean(onDownloadThumbnail || onDownloadAllThumbnail);
  }, [downloadData, onDownloadThumbnail, onDownloadAllThumbnail]);

  const urls = useMemo(() => {
    try {
      const d: any = downloadData;
      const list: string[] = [];
      const u = typeof d?.url === 'string' ? d.url : '';
      if (u) list.push(u);
      const us = Array.isArray(d?.urls) ? (d.urls as unknown[]) : [];
      for (const item of us) {
        if (typeof item === 'string' && item) list.push(item);
      }
      // uniq + keep order
      return Array.from(new Set(list));
    } catch {
      return [];
    }
  }, [downloadData]);
  const hasMultipleUrls = urls.length > 1;
  const copyUrl = urls[0] || null;
  
  // 使用 useMemo 缓存计算结果，避免每次渲染都重新计算
  const hasMultiple = useMemo(() => hasMultipleImages(downloadData), [downloadData]);
  const imageCount = useMemo(() => getImageCount(downloadData), [downloadData]);
  const showMultiMenu = hasMultiple && Boolean(onDownloadAll);
  const showThumbMenu = Boolean(onDownloadThumbnail);
  const showMenu = showMultiMenu || showThumbMenu;

  const flashDownloading = useCallback((fn?: () => void | Promise<unknown>) => {
    if (!fn) return;
    if (isDownloading) return;
    setIsDownloading(true);
    const release = () => {
      try {
        if (downloadTimerRef.current) window.clearTimeout(downloadTimerRef.current);
      } catch {}
      downloadTimerRef.current = window.setTimeout(() => {
        setIsDownloading(false);
      }, 800);
    };
    let result: unknown;
    try {
      result = fn();
    } catch {
      release();
      return;
    }
    // 异步操作（如「下载缩略图」需先在浏览器内转换图片）完成后才结束下载中状态，避免重复点击
    void Promise.resolve(result).catch(() => {}).finally(release);
  }, [isDownloading]);

  const handleDownloadAll = useCallback(() => {
    onDownloadAll?.();
  }, [onDownloadAll]);
  const handleDownloadThumbnail = useCallback(() => onDownloadThumbnail?.(), [onDownloadThumbnail]);
  const handleDownloadAllThumbnail = useCallback(() => onDownloadAllThumbnail?.(), [onDownloadAllThumbnail]);

  const handleCopyDownloadLink = useCallback(async (urlHint?: string | null) => {
    try {
      const url = String(urlHint || copyUrl || '');
      if (!url) {
        try { toast.error(t('No URL')); } catch {}
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Fallback for environments without clipboard permission
        try {
          // eslint-disable-next-line no-alert
          window.prompt(isImage ? t('Copy download link (Original)') : t('Copy download link'), url);
        } catch {}
      }
      try { toast.success(t('Copied')); } catch {}
    } catch {
      try { toast.error(t('Failed to copy download link')); } catch {}
    }
  }, [copyUrl, t, isImage]);

  const copyMenuNode = useMemo(() => {
    if (!copyUrl) return null;
    if (!isImage) {
      return (
        <DropdownMenuItem onClick={() => void handleCopyDownloadLink()} disabled={disabled}>
          {t('Copy download link')}
        </DropdownMenuItem>
      );
    }
    if (!hasMultipleUrls) {
      return (
        <DropdownMenuItem onClick={() => void handleCopyDownloadLink()} disabled={disabled}>
          {t('Copy download link (Original)')}
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={disabled}>{t('Copy download link (Original)')}</DropdownMenuSubTrigger>
        <DropdownMenuSubContent alignOffset={-4}>
          {urls.map((u, idx) => (
            <DropdownMenuItem key={`${idx}-${u}`} onClick={() => void handleCopyDownloadLink(u)} disabled={disabled}>
              {t('Image {{index}}', { index: idx + 1 })}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }, [copyUrl, disabled, handleCopyDownloadLink, hasMultipleUrls, isImage, t, urls]);

  // Keep consistent with other node-toolbar buttons (e.g. Split / History).
  const btnCls = cn("rounded-md h-8 px-2 text-xs");

  if (onDownloadOriginal) {
    // 多源下载：下拉菜单给出两个源
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className={btnCls} disabled={disabled || isDownloading}>
              {isDownloading ? <Loader2Icon size={12} className="mr-1 animate-spin text-primary" /> : <Download size={12} className="mr-1" />}
              <span>{t('Download')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-2xl min-w-[240px] p-1.5 z-[200]">
            <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer" onClick={() => flashDownloading(onDownload)} disabled={disabled || isDownloading}>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold">{t('Download source 1 (CDN link)')}</span>
                <span className="text-[10px] text-muted-foreground/60">{t('FlowMuse provides a permanent link; downloads may be slower')}</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer" onClick={() => flashDownloading(onDownloadOriginal)} disabled={disabled || isDownloading}>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold">{t('Download source 2 (Official link)')}</span>
                <span className="text-[10px] text-muted-foreground/60">{t('Provided by the model vendor; time-limited and usually faster')}</span>
              </div>
            </DropdownMenuItem>
            {copyMenuNode}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }
  
  // 单源下载：多张图片或支持缩略图时用下拉菜单（避免弹框打断）
  if (showMenu) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={btnCls} disabled={disabled || isDownloading}>
            {isDownloading ? <Loader2Icon size={12} className="mr-1 animate-spin text-primary" /> : <Download size={12} className="mr-1" />}
            <span>{t('Download')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-2xl min-w-[180px] p-1.5 z-[200]">
          <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-bold" onClick={() => flashDownloading(onDownload)} disabled={disabled || isDownloading}>
            {isImage ? (hasMultipleUrls ? t('Download original (Current image)') : t('Download original')) : t('Download')}
          </DropdownMenuItem>
          {onDownloadThumbnail ? (
            <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-bold" onClick={() => flashDownloading(handleDownloadThumbnail)} disabled={disabled || isDownloading}>
              {isImage ? (hasMultipleUrls ? t('Download thumbnail (Current image)') : t('Download thumbnail')) : t('Download Current Image (Thumbnail)')}
            </DropdownMenuItem>
          ) : null}
          {showMultiMenu ? (
            <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-bold" onClick={() => flashDownloading(handleDownloadAll)} disabled={disabled || isDownloading}>
              {isImage ? t('Download original (All images)') : t('Download All {{count}} Images', { count: imageCount })}
            </DropdownMenuItem>
          ) : null}
          {showMultiMenu && onDownloadAllThumbnail ? (
            <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-bold" onClick={() => flashDownloading(handleDownloadAllThumbnail)} disabled={disabled || isDownloading}>
              {isImage ? t('Download thumbnail (All images)') : t('Download All {{count}} Images (Thumbnail)', { count: imageCount })}
            </DropdownMenuItem>
          ) : null}
          {copyMenuNode}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // 单张：有链接则用下拉（包含复制），否则保持一键下载
  if (copyUrl) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={btnCls} disabled={disabled || isDownloading}>
            {isDownloading ? <Loader2Icon size={12} className="mr-1 animate-spin text-primary" /> : <Download size={12} className="mr-1" />}
            <span>{t('Download')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-2xl min-w-[160px] p-1.5 z-[200]">
          <DropdownMenuItem className="rounded-lg py-2.5 px-3 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-bold" onClick={() => flashDownloading(onDownload)} disabled={disabled || isDownloading}>
            {isImage ? t('Download original') : t('Download')}
          </DropdownMenuItem>
          {copyMenuNode}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // 单张：直接下载
  return (
    <Button
      variant="ghost"
      size="sm"
      className={btnCls}
      onClick={() => flashDownloading(onDownload)}
      disabled={disabled || isDownloading}
    >
      {isDownloading ? <Loader2Icon size={12} className="mr-1 animate-spin text-primary" /> : <Download size={12} className="mr-1" />}
      <span>{t('Download')}</span>
    </Button>
  );
}
